import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button, Input, Card } from './ui';
import logoDefault from '../assets/almaquinta.svg';

const Login = () => {
    const { login, error } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [loading, setLoading] = useState(false);
    const [localError, setLocalError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLocalError('');
        setLoading(true);
        const result = await login(email, password, rememberMe);
        if (!result.success) setLocalError(result.error || 'Error al iniciar sesion');
        setLoading(false);
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 bg-bg">
            {/* Decoracion sutil de fondo solo en dark */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="hidden dark:block absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-primary/10 blur-3xl" />
                <div className="hidden dark:block absolute -bottom-40 -right-40 w-[480px] h-[480px] rounded-full bg-purple2/10 blur-3xl" />
            </div>

            <Card className="relative w-full max-w-md">
                <Card.Body className="p-8 sm:p-10">
                    {/* Header */}
                    <div className="flex flex-col items-center text-center mb-8">
                        <div className="w-20 h-20 rounded-2xl bg-white/95 p-2 shadow-soft mb-4 inline-flex items-center justify-center">
                            <img src={logoDefault} alt="Don Presupuesto" className="w-full h-full object-contain" />
                        </div>
                        <h1 className="text-2xl font-bold text-text">Don Presupuesto</h1>
                        <p className="text-sm text-muted mt-1">Ingresa tus credenciales para continuar</p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                        {(localError || error) && (
                            <div className="px-3 py-2.5 rounded-lg bg-danger/10 border border-danger/30 text-danger text-sm">
                                {localError || error}
                            </div>
                        )}

                        <Input
                            type="email"
                            label="Email"
                            placeholder="tu@email.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoFocus
                            prefix={<Mail size={16} />}
                        />

                        <Input
                            type={showPassword ? 'text' : 'password'}
                            label="Contraseña"
                            placeholder="Tu contraseña"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            prefix={<Lock size={16} />}
                            suffix={
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(v => !v)}
                                    tabIndex={-1}
                                    className="text-muted hover:text-text transition"
                                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            }
                        />

                        <div className="flex items-center justify-between gap-2 flex-wrap">
                            <label className="inline-flex items-center gap-2 text-sm text-muted cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    className="w-4 h-4 rounded border-line accent-primary"
                                />
                                <span>Mantener sesión iniciada</span>
                            </label>
                            <Link
                                to="/forgot-password"
                                className="text-sm text-primary hover:underline"
                            >
                                ¿Olvidaste tu contraseña?
                            </Link>
                        </div>

                        <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            fullWidth
                            disabled={loading}
                            className="mt-2"
                        >
                            {loading ? 'Ingresando...' : 'Ingresar'}
                        </Button>
                    </form>
                </Card.Body>
            </Card>
        </div>
    );
};

export default Login;
